import { useSeo } from '../hooks/useSeo.ts';

const LAST_UPDATED = '2026-05-22';

/**
 * Plain-English GDPR-compliant privacy notice. Covers: what data we collect, why, lawful
 * basis, third parties, retention, transfers, and your rights. Tailored to what this
 * product actually does — generic privacy-policy templates leave footguns in places they
 * don't expect (e.g. claiming we don't use AI when we send violation HTML to Claude).
 *
 * Update LAST_UPDATED whenever the substantive content changes — that's the GDPR
 * notification trigger.
 */
export default function PrivacyPage() {
  useSeo({
    title: 'Privacy policy',
    canonicalPath: '/privacy',
    description:
      "EAA Proof's privacy policy: what data we collect (URL, hashed email, scan results), where it lives (EU-hosted), your GDPR rights, and retention windows.",
  });

  return (
    <div className="page legal-page">
      <header>
        <h1>Privacy policy</h1>
        <p className="muted">Last updated: {LAST_UPDATED}</p>
      </header>

      <section>
        <p>
          EAA Proof ("we", "us") provides a web-based accessibility scanning service. This
          notice describes how we process personal data under the EU General Data Protection
          Regulation (GDPR) and the UK GDPR. It applies to <strong>eaaproof.com</strong> and
          the service available there.
        </p>
        <p>
          Data controller: EAA Proof. Contact for any privacy question or rights request:{' '}
          <a href="mailto:support@eaaproof.com">support@eaaproof.com</a>.
        </p>
      </section>

      <section>
        <h2>1. What we collect and why</h2>
        <h3>Account data</h3>
        <p>
          When you sign up — with Google or email + password — we store your email address,
          display name, profile picture (Google only), and a hashed password (email signup
          only). Lawful basis: <em>performance of a contract</em> (Art. 6(1)(b) GDPR). Without
          this we cannot provide the service to you.
        </p>

        <h3>Scan inputs and outputs</h3>
        <p>
          When you submit a URL for scanning, we record the URL, the discovered pages, the
          accessibility findings produced by axe-core, screenshots, the generated PDF report,
          and the generated accessibility statement HTML. Lawful basis: <em>performance of a
          contract</em>.
        </p>

        <h3>Billing data</h3>
        <p>
          We rely on Stripe to process payments. Stripe collects your billing address, name,
          tax ID (if provided), and card details directly — we never see or store card
          numbers. We store the Stripe Customer ID and the event log of your purchases.
          Lawful basis: <em>performance of a contract</em> and <em>legal obligation</em>{' '}
          (Art. 6(1)(c), EU VAT invoicing rules).
        </p>

        <h3>Operational logs</h3>
        <p>
          Server logs (IP address, request path, user agent, error stack traces) are retained
          for up to 30 days for debugging and abuse detection. Lawful basis: <em>legitimate
          interests</em> (Art. 6(1)(f)) — running a secure service.
        </p>
      </section>

      <section>
        <h2>2. Third-party processors</h2>
        <p>
          We do not sell or share your data for advertising. We use the following
          sub-processors strictly to deliver the service:
        </p>
        <ul>
          <li>
            <strong>Stripe</strong> (Ireland / US) — payment processing + tax compliance.
          </li>
          <li>
            <strong>Cloudflare R2</strong> (EU region) — storage of generated PDF reports and
            accessibility statement HTML files.
          </li>
          <li>
            <strong>Anthropic</strong> (US) — we send anonymised accessibility-violation HTML
            snippets to Claude to produce plain-English remediation text. We do NOT send your
            email, account identifiers, or full pages — only the snippets axe-core flagged.
            Anthropic does not retain this data for training.
          </li>
          <li>
            <strong>Google</strong> (Ireland) — OAuth sign-in (only if you choose "Continue
            with Google").
          </li>
          <li>
            <strong>Resend</strong> (US) — transactional email delivery (verification + password
            reset). They process your email address to deliver these messages and nothing else.
          </li>
          <li>
            <strong>Hetzner / Coolify</strong> (EU) — hosting infrastructure for the API,
            database, and SPA.
          </li>
        </ul>
        <p>
          A current list of sub-processors and our standard contractual clauses (SCCs) for
          US transfers is available on request.
        </p>
      </section>

      <section>
        <h2>3. Retention</h2>
        <ul>
          <li>Account data: as long as your account is active.</li>
          <li>
            Scan results (URL, findings, PDFs, statement HTML): as long as your account is
            active, so you can return to past audits.
          </li>
          <li>
            Billing records: 10 years after the last invoice, as required by EU VAT law.
            This is the only data we keep after account deletion.
          </li>
          <li>Server logs: 30 days.</li>
        </ul>
      </section>

      <section>
        <h2>4. Cookies and similar technologies</h2>
        <p>
          Two cookies are <strong>strictly necessary</strong> and set without consent under
          ePrivacy Directive Art. 5(3): the sign-in cookie (<code>.eaa.auth</code>) and the
          CSRF protection cookie (<code>eaa-csrf</code>). A third cookie (<code>eaa-consent</code>)
          records your cookie-consent choice so we don't ask again on every visit; it stores
          only your decision plus a timestamp.
        </p>
        <p>
          <strong>Analytics and advertising cookies are set only after you opt in</strong> via
          the consent banner. If you accept analytics, Google Analytics 4 sets <code>_ga</code>{' '}
          and <code>_ga_*</code> cookies for aggregated traffic measurement. If you accept
          advertising, Google Ads sets conversion-measurement cookies so we can tell which ads
          deliver real customers. You can change or withdraw consent at any time via the
          "Cookie settings" link in the site footer.
        </p>
        <p>
          We use Google Consent Mode v2 with default-deny on first visit, which means Google's
          scripts are loaded but cannot read or write storage until you grant permission.
        </p>
      </section>

      <section>
        <h2>5. Your rights</h2>
        <p>Under GDPR you can, at any time:</p>
        <ul>
          <li>
            <strong>Access</strong> the personal data we hold about you (Art. 15). Use the
            "Export my data" button on your profile, or email us. We respond within 30 days.
          </li>
          <li>
            <strong>Erase</strong> your account and associated data (Art. 17). Use the
            "Delete my account" button on your profile, or email us.
          </li>
          <li>
            <strong>Rectify</strong> inaccurate data (Art. 16) — edit it in your profile or
            email us.
          </li>
          <li>
            <strong>Object</strong> to processing based on legitimate interests (Art. 21).
          </li>
          <li>
            <strong>Port</strong> your data to another provider in a machine-readable format
            (Art. 20) — the same export endpoint serves both rights.
          </li>
          <li>
            <strong>Lodge a complaint</strong> with your national supervisory authority. A
            list is at{' '}
            <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer">
              edpb.europa.eu
            </a>.
          </li>
        </ul>
      </section>

      <section>
        <h2>6. International transfers</h2>
        <p>
          Some sub-processors (Stripe, Anthropic, Resend) operate in the US. Transfers rely
          on Standard Contractual Clauses (SCCs) plus, where applicable, the EU-US Data
          Privacy Framework adequacy decision.
        </p>
      </section>

      <section>
        <h2>7. Security</h2>
        <p>
          Passwords are stored as PBKDF2 hashes (never plaintext). Data in transit is
          TLS-encrypted. We do not store payment card data — that lives only in Stripe's
          PCI-DSS Level 1 environment. Access to production systems is restricted to the
          minimum operators required.
        </p>
        <p>
          If we ever suffer a data breach affecting your personal data, we will notify the
          competent supervisory authority within 72 hours and you personally without undue
          delay where the breach is likely to result in a high risk to your rights and
          freedoms (Art. 33–34 GDPR).
        </p>
      </section>

      <section>
        <h2>8. Changes</h2>
        <p>
          We will update this notice when our processing changes. The "Last updated" date at
          the top of this page reflects the most recent material change. If a change affects
          your rights or the data we collect, we will notify you by email or by an in-product
          notice before it takes effect.
        </p>
      </section>
    </div>
  );
}
